import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import {
  Card,
  Stack,
  Container,
  Typography,
  CircularProgress,
  Box,
  Button,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Scrollbar from "../components/scrollbar";
import TenantsTable from "src/components/tenants/TenantsTable";
import TenantDialog from "src/components/tenants/TenantDialog";
import { get, del } from "src/utils/networkUtils";

export default function TenantsPage() {
  const [loading, setLoading] = useState(false);
  const [tenants, setTenants] = useState(null);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);

  const loadTenants = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await get(`/admin/tenants`);
      const tenants = response.tenants || [];

      // Load competitions for each tenant
      const tenantsWithCompetitions = await Promise.all(
        tenants.map(async (tenant) => {
          try {
            const competitionsResponse = await get(`/admin/tenants/${tenant.tenantId}/tenant-competitions`);
            return {
              ...tenant,
              competitions: competitionsResponse.tenantCompetitions || []
            };
          } catch (error) {
            console.error(`Error loading competitions for tenant ${tenant.tenantId}:`, error);
            return {
              ...tenant,
              competitions: []
            };
          }
        })
      );

      setTenants(tenantsWithCompetitions);
      setLoading(false);
    } catch (error) {
      console.error("Error loading tenants:", error);
      setError("Failed to load tenants");
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTenants();
  }, []);

  const handleEdit = (tenant) => {
    setSelectedTenant(tenant);
    setDialogOpen(true);
  };

  const handleDelete = async (tenant) => {
    if (window.confirm(`Are you sure you want to delete "${tenant.name}"?`)) {
      try {
        await del(`/admin/tenants/${tenant.tenantId}`);
        loadTenants(); // Reload the list
      } catch (error) {
        console.error("Error deleting tenant:", error);
        setError("Failed to delete tenant");
      }
    }
  };

  const handleAddNew = () => {
    setSelectedTenant(null);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedTenant(null);
  };

  const handleDialogSuccess = () => {
    setDialogOpen(false);
    setSelectedTenant(null);
    loadTenants(); // Reload the list
  };

  return (
    <>
      <Helmet>
        <title> Tenants </title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Tenants
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
          >
            Add Tenant
          </Button>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Card>
          {tenants === null || loading ? (
            <Box
              sx={{ display: "flex", justifyContent: "center" }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Scrollbar>
              <TenantsTable
                tenants={tenants}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            </Scrollbar>
          )}
        </Card>
      </Container>

      <TenantDialog
        open={dialogOpen}
        tenant={selectedTenant}
        onClose={handleDialogClose}
        onSuccess={handleDialogSuccess}
      />
    </>
  );
}
